import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  type ComponentProps,
  type CSSProperties,
  cloneElement,
  createContext,
  Fragment,
  type ReactElement,
  type ReactNode,
  use,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  type GestureResponderEvent,
  I18nManager,
  type LayoutChangeEvent,
  Modal,
  Platform,
  Pressable,
  type PressableProps,
  ScrollView,
  type StyleProp,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';
import Animated, {
  Easing,
  ReduceMotion,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { StyleSheet } from 'react-native-unistyles';
import FooterActionButton from '@/components/Custom/FooterActionButton';
import Icon from '@/components/Unistyles/Icon';
import useHaptics from '@/hooks/useHaptics';
import { toggleSelection } from '@/utils/selection';
import Box from './Box';
import CheckboxRow from './CheckboxRow';
import {
  getAnchoredMenuLayout,
  getRectangleRelativeToViewport,
  MENU_ANIMATION_DURATION_MS,
  MENU_ANIMATION_INITIAL_SCALE,
  MENU_ANIMATION_OFFSET,
  type MenuAlignment,
  type MenuRectangle,
  type MenuSide,
  type MenuSize,
} from './Menu.utils';
import Text from './Text';

const AnimatedBox = Animated.createAnimatedComponent(Box);

export type MenuAction = {
  actions?: readonly MenuAction[];
  checked?: boolean;
  destructive?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  icon?: ComponentProps<typeof Icon>['name'];
  id: string;
  onSelect?: () => Promise<void> | void;
  testID?: string;
  title: string;
};

export type MenuTriggerProps = {
  accessibilityLabel?: PressableProps['accessibilityLabel'];
  accessibilityRole?: PressableProps['accessibilityRole'];
  accessibilityState?: PressableProps['accessibilityState'];
  hitSlop?: PressableProps['hitSlop'];
  onLayout?: PressableProps['onLayout'];
  onPress?: PressableProps['onPress'];
  onPressIn?: PressableProps['onPressIn'];
  onTouchStart?: PressableProps['onTouchStart'];
  testID?: PressableProps['testID'];
};

export type MenuProps = {
  actions: readonly MenuAction[];
  align?: MenuAlignment;
  children: (props: MenuTriggerProps) => ReactElement<MenuTriggerProps>;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  title?: string;
};

export type MenuCheckboxOption = {
  disabled?: boolean;
  id: string;
  label: string;
};

export type MenuCheckboxSection<TOption extends MenuCheckboxOption> = {
  allLabel: string;
  emptyLabel: string;
  id: string;
  options: readonly TOption[];
  renderLeading?: (option: TOption) => ReactNode;
  selectedIds: readonly string[];
  title: string;
  onSelectedIdsChange: (ids: string[]) => void;
};

type CloseReason = 'dismiss' | 'select';
type AlignX = MenuAlignment;
type AlignY = MenuSide;

type RootProps = {
  children: ReactNode;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
};

type TriggerProps = {
  children: (props: MenuTriggerProps) => ReactElement<MenuTriggerProps>;
};

type ContentProps = {
  children: ReactNode;
  scroll?: boolean;
  testID?: string;
  x?: AlignX;
  y?: AlignY;
};

type LabelProps = {
  children: ReactNode;
};

type ItemChildProps = {
  accessibilityLabel?: PressableProps['accessibilityLabel'];
  disabled?: boolean;
  onPress?: () => void;
  testID?: string;
};

type ItemSelectHandler = (reason: CloseReason) => Promise<void> | void;
type ItemProps =
  | {
      accessibilityLabel?: PressableProps['accessibilityLabel'];
      asChild: true;
      children: ReactElement<ItemChildProps>;
      disabled?: boolean;
      onSelect?: ItemSelectHandler;
      testID?: string;
    }
  | {
      accessibilityLabel?: PressableProps['accessibilityLabel'];
      asChild?: false;
      children: ReactNode;
      disabled?: boolean;
      onSelect?: ItemSelectHandler;
      testID?: string;
    };

type ItemTitleProps = {
  children: ReactNode;
};

type AnchoredMenuContextValue = {
  anchor: MenuRectangle | null;
  cacheAnchor: (anchor: MenuRectangle) => void;
  close: () => void;
  open: boolean;
  openAt: (anchor: MenuRectangle) => void;
  openVersion: number;
  setOpen: (open: boolean) => void;
};

const AnchoredMenuContext = createContext<AnchoredMenuContextValue | null>(
  null,
);

function useAnchoredMenu() {
  const context = use(AnchoredMenuContext);

  if (!context) {
    throw new Error(
      'AnchoredMenu components must be used within AnchoredMenu.Root',
    );
  }

  return context;
}

function AnchoredRoot({
  children,
  defaultOpen = false,
  onOpenChange,
  open: controlledOpen,
}: RootProps) {
  const [anchor, setAnchor] = useState<MenuRectangle | null>(null);
  const [openVersion, setOpenVersion] = useState(0);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;
  const anchorRequestRef = useRef<MenuRectangle | null>(null);
  const lastMeasuredAnchorRef = useRef<MenuRectangle | null>(null);
  const openRequestRef = useRef(open);

  useEffect(() => {
    openRequestRef.current = open;

    if (!open) {
      anchorRequestRef.current = null;
      setAnchor(null);
      return;
    }

    const lastMeasuredAnchor = lastMeasuredAnchorRef.current;

    if (!anchorRequestRef.current && lastMeasuredAnchor) {
      anchorRequestRef.current = lastMeasuredAnchor;
      setAnchor(lastMeasuredAnchor);
      setOpenVersion((currentVersion) => currentVersion + 1);
    }
  }, [open]);

  const cacheAnchor = (nextAnchor: MenuRectangle) => {
    lastMeasuredAnchorRef.current = nextAnchor;

    if (!openRequestRef.current || anchorRequestRef.current) {
      return;
    }

    anchorRequestRef.current = nextAnchor;
    setAnchor(nextAnchor);
    setOpenVersion((currentVersion) => currentVersion + 1);
  };

  const setOpen = (nextOpen: boolean) => {
    if (nextOpen === openRequestRef.current) {
      return;
    }

    openRequestRef.current = nextOpen;

    if (controlledOpen === undefined) {
      setUncontrolledOpen(nextOpen);
    }

    onOpenChange?.(nextOpen);
  };

  const close = () => {
    setOpen(false);
  };

  const openAt = (nextAnchor: MenuRectangle) => {
    if (openRequestRef.current && anchorRequestRef.current) {
      return;
    }

    lastMeasuredAnchorRef.current = nextAnchor;
    anchorRequestRef.current = nextAnchor;
    setAnchor(nextAnchor);
    setOpenVersion((currentVersion) => currentVersion + 1);

    if (!openRequestRef.current) {
      setOpen(true);
    }
  };

  return (
    <AnchoredMenuContext
      value={{
        anchor,
        cacheAnchor,
        close,
        open,
        openAt,
        openVersion,
        setOpen,
      }}
    >
      {children}
    </AnchoredMenuContext>
  );
}

function WebRoot({ children, defaultOpen, onOpenChange, open }: RootProps) {
  return (
    <RadixDropdownMenu.Root
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={onOpenChange}
    >
      {children}
    </RadixDropdownMenu.Root>
  );
}

export function Root(props: RootProps) {
  return Platform.OS === 'web' ? (
    <WebRoot {...props} />
  ) : (
    <AnchoredRoot {...props} />
  );
}

function AnchoredTrigger({ children }: TriggerProps) {
  return <AnchoredTriggerHost>{children({ hitSlop: 8 })}</AnchoredTriggerHost>;
}

function AnchoredTriggerHost({
  children,
}: {
  children: ReactElement<MenuTriggerProps>;
}) {
  const { cacheAnchor, open, openAt } = useAnchoredMenu();

  const measureAndOpen = (event: GestureResponderEvent) => {
    const trigger = event.currentTarget;

    trigger.measureInWindow((x, y, width, height) => {
      const rectangle = { height, width, x, y };

      if (isValidRectangle(rectangle)) {
        openAt(rectangle);
      }
    });
  };

  const onPressIn = (event: GestureResponderEvent) => {
    children.props.onPressIn?.(event);
    measureAndOpen(event);
  };

  const onLayout = (event: LayoutChangeEvent) => {
    children.props.onLayout?.(event);

    event.currentTarget.measureInWindow((x, y, width, height) => {
      const rectangle = { height, width, x, y };

      if (isValidRectangle(rectangle)) {
        cacheAnchor(rectangle);
      }
    });
  };

  const onPress = useHaptics<[GestureResponderEvent]>((event) => {
    children.props.onPress?.(event);
    measureAndOpen(event);
  });

  return cloneElement(children, {
    ...children.props,
    accessibilityRole: children.props.accessibilityRole ?? 'button',
    accessibilityState: {
      ...children.props.accessibilityState,
      expanded: open,
    },
    hitSlop: children.props.hitSlop ?? 8,
    onLayout,
    onPress,
    onPressIn,
  });
}

function WebTrigger({ children }: TriggerProps) {
  return (
    <RadixDropdownMenu.Trigger asChild>
      {children({})}
    </RadixDropdownMenu.Trigger>
  );
}

export function Trigger(props: TriggerProps) {
  return Platform.OS === 'web' ? (
    <WebTrigger {...props} />
  ) : (
    <AnchoredTrigger {...props} />
  );
}

type VersionedRectangle = {
  rectangle: MenuRectangle;
  version: number;
};

type VersionedSize = {
  size: MenuSize;
  version: number;
};

function AnchoredContent({
  children,
  testID,
  x: align = 'right',
  y: preferredSide = 'bottom',
}: ContentProps) {
  const { anchor, close, open, openVersion } = useAnchoredMenu();
  const [relativeAnchor, setRelativeAnchor] =
    useState<VersionedRectangle | null>(null);
  const [surfaceMeasurement, setSurfaceMeasurement] =
    useState<VersionedSize | null>(null);
  const [viewportSize, setViewportSize] = useState<MenuSize | null>(null);
  const animationProgress = useSharedValue(0);
  const viewportRef = useRef<View>(null);

  useEffect(() => {
    if (!open || !anchor || !viewportSize) {
      return;
    }

    const measurementVersion = openVersion;
    const animationFrame = requestAnimationFrame(() => {
      viewportRef.current?.measureInWindow((x, y, width, height) => {
        const viewport = { height, width, x, y };

        if (!isValidRectangle(viewport)) {
          return;
        }

        setRelativeAnchor({
          rectangle: getRectangleRelativeToViewport({
            rectangle: anchor,
            viewport,
          }),
          version: measurementVersion,
        });
      });
    });

    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [anchor, open, openVersion, viewportSize]);

  const onViewportLayout = (event: LayoutChangeEvent) => {
    const { height, width } = event.nativeEvent.layout;

    setViewportSize((currentSize) =>
      currentSize?.height === height && currentSize.width === width
        ? currentSize
        : { height, width },
    );
  };

  const onSurfaceLayout = (event: LayoutChangeEvent) => {
    const { height, width } = event.nativeEvent.layout;

    setSurfaceMeasurement((currentMeasurement) =>
      currentMeasurement?.version === openVersion &&
      currentMeasurement.size.height === height &&
      currentMeasurement.size.width === width
        ? currentMeasurement
        : { size: { height, width }, version: openVersion },
    );
  };

  const hasCurrentMeasurements =
    relativeAnchor?.version === openVersion &&
    surfaceMeasurement?.version === openVersion &&
    viewportSize !== null;
  const menuLayout = hasCurrentMeasurements
    ? getAnchoredMenuLayout({
        align,
        anchor: relativeAnchor.rectangle,
        isRTL: I18nManager.isRTL,
        preferredSide,
        surface: surfaceMeasurement.size,
        viewport: viewportSize,
      })
    : null;
  const maximumSurfaceWidth = Math.max(
    0,
    Math.min(320, (viewportSize?.width ?? 336) - 16),
  );
  const maximumSurfaceHeight =
    menuLayout?.maxHeight ?? Math.max(0, (viewportSize?.height ?? 640) - 16);
  const surfaceReady = menuLayout !== null && maximumSurfaceHeight > 0;
  const animationOffset =
    menuLayout?.resolvedSide === 'top'
      ? MENU_ANIMATION_OFFSET
      : -MENU_ANIMATION_OFFSET;
  const animatedSurfaceStyle = useAnimatedStyle(
    () => ({
      opacity: animationProgress.value,
      transform: [
        {
          translateY: animationOffset * (1 - animationProgress.value),
        },
        {
          scale:
            MENU_ANIMATION_INITIAL_SCALE +
            (1 - MENU_ANIMATION_INITIAL_SCALE) * animationProgress.value,
        },
      ],
    }),
    [animationOffset],
  );

  useEffect(() => {
    if (!open || !surfaceReady) {
      animationProgress.set(0);
      return;
    }

    animationProgress.set(
      withTiming(1, {
        duration: MENU_ANIMATION_DURATION_MS,
        easing: Easing.out(Easing.cubic),
        reduceMotion: ReduceMotion.System,
      }),
    );
  }, [animationProgress, open, surfaceReady]);

  return (
    <Modal
      animationType="none"
      hardwareAccelerated
      navigationBarTranslucent
      onRequestClose={close}
      presentationStyle="overFullScreen"
      statusBarTranslucent
      transparent
      visible={open && anchor !== null}
    >
      <View style={styles.anchoredOverlay}>
        <Pressable
          accessible={false}
          onPress={close}
          style={styles.anchoredBackdrop}
          testID={testID ? `${testID}Backdrop` : undefined}
        />
        <View pointerEvents="box-none" style={styles.anchoredSafeArea}>
          <View
            ref={viewportRef}
            collapsable={false}
            onLayout={onViewportLayout}
            pointerEvents="box-none"
            style={styles.anchoredViewport}
          >
            <AnimatedBox
              key={openVersion}
              accessibilityElementsHidden={!surfaceReady}
              accessibilityRole="menu"
              accessibilityViewIsModal
              background="surface"
              border="muted"
              importantForAccessibility={
                surfaceReady ? 'yes' : 'no-hide-descendants'
              }
              onLayout={onSurfaceLayout}
              pointerEvents={surfaceReady ? 'auto' : 'none'}
              style={[
                styles.anchoredSurface,
                {
                  maxHeight: maximumSurfaceHeight,
                  maxWidth: maximumSurfaceWidth,
                  minWidth: Math.min(220, maximumSurfaceWidth),
                },
                menuLayout
                  ? styles.anchoredSurfacePosition(menuLayout)
                  : styles.anchoredUnpositionedSurface,
                surfaceReady ? animatedSurfaceStyle : null,
              ]}
              testID={testID}
            >
              <ScrollView
                contentContainerStyle={styles.anchoredContent}
                keyboardShouldPersistTaps="handled"
                nestedScrollEnabled
                showsVerticalScrollIndicator
              >
                {children}
              </ScrollView>
            </AnimatedBox>
          </View>
        </View>
      </View>
    </Modal>
  );
}

function WebContent({
  children,
  scroll = false,
  testID,
  x = 'left',
  y = 'bottom',
}: ContentProps) {
  return (
    <RadixDropdownMenu.Portal>
      <RadixDropdownMenu.Content
        align={toRadixAlign(x)}
        collisionPadding={8}
        data-testid={testID}
        onClick={(event) => event.stopPropagation()}
        loop
        side={y}
        sideOffset={8}
        style={webContentStyle}
      >
        <WebMenuSurface scroll={scroll} side={y}>
          {children}
        </WebMenuSurface>
      </RadixDropdownMenu.Content>
    </RadixDropdownMenu.Portal>
  );
}

function WebMenuSurface({
  children,
  scroll,
  side,
}: {
  children: ReactNode;
  scroll: boolean;
  side: AlignY;
}) {
  const animationProgress = useSharedValue(0);
  const animationOffset =
    side === 'top' ? MENU_ANIMATION_OFFSET : -MENU_ANIMATION_OFFSET;
  const animatedStyle = useAnimatedStyle(
    () => ({
      opacity: animationProgress.value,
      transform: [
        {
          translateY: animationOffset * (1 - animationProgress.value),
        },
        {
          scale:
            MENU_ANIMATION_INITIAL_SCALE +
            (1 - MENU_ANIMATION_INITIAL_SCALE) * animationProgress.value,
        },
      ],
    }),
    [animationOffset],
  );

  useEffect(() => {
    animationProgress.set(
      withTiming(1, {
        duration: MENU_ANIMATION_DURATION_MS,
        easing: Easing.out(Easing.cubic),
        reduceMotion: ReduceMotion.System,
      }),
    );
  }, [animationProgress]);

  return (
    <AnimatedBox
      background="surface"
      border="muted"
      style={[styles.webMenu, animatedStyle]}
    >
      {scroll ? (
        <ScrollView nestedScrollEnabled style={styles.webScrollContent}>
          {children}
        </ScrollView>
      ) : (
        children
      )}
    </AnimatedBox>
  );
}

export function Content(props: ContentProps) {
  return Platform.OS === 'web' ? (
    <WebContent {...props} />
  ) : (
    <AnchoredContent {...props} />
  );
}

export function Label({ children }: LabelProps) {
  const label = <Text style={styles.label}>{children}</Text>;

  return Platform.OS === 'web' ? (
    <RadixDropdownMenu.Label>{label}</RadixDropdownMenu.Label>
  ) : (
    label
  );
}

function NativeItem({
  accessibilityLabel,
  children,
  disabled = false,
  onSelect,
  asChild,
  testID,
}: ItemProps) {
  const { close } = useAnchoredMenu();

  const onPress = () => {
    if (disabled) {
      return;
    }

    close();

    if (onSelect) {
      requestAnimationFrame(() => {
        void onSelect('select');
      });
    }
  };

  if (asChild) {
    return cloneElement(children, {
      ...children.props,
      accessibilityLabel,
      disabled,
      onPress,
      ...(testID ? { testID } : {}),
    });
  }

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="menuitem"
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      style={({ pressed }) => styles.nativeItem({ disabled, pressed })}
      testID={testID}
    >
      {children}
    </Pressable>
  );
}

type WebItemStyleOptions = {
  disabled: boolean;
  highlighted: boolean;
};

function WebItem({
  children,
  disabled = false,
  onSelect,
  asChild,
  testID,
}: ItemProps) {
  const [highlighted, setHighlighted] = useState(false);
  const onRadixSelect = () => {
    if (disabled) {
      return;
    }

    requestAnimationFrame(() => {
      void onSelect?.('select');
    });
  };

  if (asChild) {
    return (
      <RadixDropdownMenu.Item
        asChild
        disabled={disabled}
        onSelect={onRadixSelect}
      >
        {cloneElement(children, {
          ...children.props,
          disabled,
          ...(testID ? { testID } : {}),
        })}
      </RadixDropdownMenu.Item>
    );
  }

  return (
    <RadixDropdownMenu.Item
      data-testid={testID}
      disabled={disabled}
      onBlur={() => {
        setHighlighted(false);
      }}
      onFocus={() => {
        setHighlighted(true);
      }}
      onPointerEnter={() => {
        setHighlighted(true);
      }}
      onPointerLeave={() => {
        setHighlighted(false);
      }}
      onSelect={onRadixSelect}
      style={webItemStyle({ disabled, highlighted })}
    >
      {children}
    </RadixDropdownMenu.Item>
  );
}

export function Item(props: ItemProps) {
  return Platform.OS === 'web' ? (
    <WebItem {...props} />
  ) : (
    <NativeItem {...props} />
  );
}

export function ItemTitle({ children }: ItemTitleProps) {
  return <Text style={styles.itemTitle}>{children}</Text>;
}

function visibleActions(actions: readonly MenuAction[]) {
  return actions.filter((action) => !action.hidden);
}

function renderActionContent(action: MenuAction) {
  const iconVariant = action.destructive
    ? 'error'
    : action.disabled
      ? 'typographyMuted'
      : 'typography';

  return (
    <>
      {action.icon ? (
        <Icon accessible={false} name={action.icon} variant={iconVariant} />
      ) : null}
      <Text
        numberOfLines={1}
        style={styles.actionTitle({
          destructive: action.destructive,
          disabled: action.disabled,
        })}
      >
        {action.title}
      </Text>
      {action.checked ? (
        <Box flex alignItems="flex-end">
          <Icon
            accessible={false}
            name="checkmark-outline"
            size="lg"
            variant="primary"
          />
        </Box>
      ) : null}
    </>
  );
}

function renderActions(actions: readonly MenuAction[]) {
  return visibleActions(actions).map((action) => {
    const childActions = action.actions ? visibleActions(action.actions) : [];

    if (childActions.length > 0) {
      return (
        <Fragment key={action.id}>
          <Label>{action.title}</Label>
          {renderActions(childActions)}
        </Fragment>
      );
    }

    return (
      <Item
        key={action.id}
        accessibilityLabel={action.title}
        disabled={action.disabled}
        onSelect={() => {
          void action.onSelect?.();
        }}
        testID={action.testID}
      >
        {renderActionContent(action)}
      </Item>
    );
  });
}

export default function Menu({
  actions,
  align = 'right',
  children,
  style,
  testID,
  title,
}: MenuProps) {
  return (
    <Box style={style} testID={testID}>
      <Root>
        <Trigger>{children}</Trigger>
        <Content x={align} y="bottom">
          {title ? <Label>{title}</Label> : null}
          {renderActions(actions)}
        </Content>
      </Root>
    </Box>
  );
}

type ModalMenuTriggerChildProps = {
  accessibilityLabel?: PressableProps['accessibilityLabel'];
  accessibilityRole?: PressableProps['accessibilityRole'];
  hitSlop?: PressableProps['hitSlop'];
  onPress?: PressableProps['onPress'];
  onPressIn?: PressableProps['onPressIn'];
  testID?: PressableProps['testID'];
};

type ModalMenuContextValue = {
  close: () => void;
  open: boolean;
  openMenu: () => void;
  setOpen: (open: boolean) => void;
};

const ModalMenuContext = createContext<ModalMenuContextValue | null>(null);

function useModalMenu() {
  const context = use(ModalMenuContext);

  if (!context) {
    throw new Error('ModalMenu components must be used within ModalMenu.Root');
  }

  return context;
}

function ModalMenuRoot({
  children,
  defaultOpen = false,
  onOpenChange,
  open: controlledOpen,
}: RootProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;

  const setOpen = (nextOpen: boolean) => {
    if (controlledOpen === undefined) {
      setUncontrolledOpen(nextOpen);
    }

    onOpenChange?.(nextOpen);
  };

  const close = () => {
    setOpen(false);
  };

  const openMenu = () => {
    setOpen(true);
  };

  return (
    <ModalMenuContext value={{ close, open, openMenu, setOpen }}>
      {children}
    </ModalMenuContext>
  );
}

function ModalMenuTrigger({
  children,
}: {
  children: (props: ModalMenuTriggerChildProps) => ReactElement;
}) {
  const { openMenu } = useModalMenu();

  const onPressIn = () => {
    openMenu();
  };

  const onPress = useHaptics<[GestureResponderEvent]>(() => {
    openMenu();
  });

  return children({
    hitSlop: 8,
    onPress,
    onPressIn,
  });
}

type ModalSurfaceImplementationProps = {
  children: ReactNode;
  close: () => void;
  menuContext: ModalMenuContextValue;
  open: boolean;
  testID?: string;
};

function ModalMenuSurface({
  children,
  testID,
}: {
  children: ReactNode;
  testID?: string;
}) {
  const menuContext = useModalMenu();
  const { close, open } = menuContext;

  if (Platform.OS !== 'web') {
    return (
      <NativeBottomSheetSurface
        close={close}
        menuContext={menuContext}
        open={open}
      >
        {children}
      </NativeBottomSheetSurface>
    );
  }

  return (
    <DesktopModalSurface
      close={close}
      menuContext={menuContext}
      open={open}
      testID={testID}
    >
      {children}
    </DesktopModalSurface>
  );
}

function NativeBottomSheetSurface({
  children,
  close,
  menuContext,
  open,
}: ModalSurfaceImplementationProps) {
  const sheetRef = useRef<BottomSheetModal>(null);
  const hasPresentedRef = useRef(false);
  const windowDimensions = useWindowDimensions();
  const maxDynamicContentSize = Math.max(240, windowDimensions.height * 0.86);
  const renderBackdrop = (props: BottomSheetBackdropProps) => (
    <BottomSheetBackdrop
      appearsOnIndex={0}
      disappearsOnIndex={-1}
      pressBehavior="close"
      {...props}
    />
  );
  const syncClosedState = () => {
    hasPresentedRef.current = false;
    close();
  };
  const handleSheetChange = (index: number) => {
    if (index < 0) {
      syncClosedState();
    }
  };

  useEffect(() => {
    if (open) {
      const frame = requestAnimationFrame(() => {
        sheetRef.current?.present();
        hasPresentedRef.current = true;
      });

      return () => {
        cancelAnimationFrame(frame);
      };
    }

    if (hasPresentedRef.current) {
      sheetRef.current?.dismiss();
      hasPresentedRef.current = false;
    }
  }, [open]);

  return (
    <BottomSheetModal
      ref={sheetRef}
      index={0}
      backdropComponent={renderBackdrop}
      enableDynamicSizing
      enablePanDownToClose
      maxDynamicContentSize={maxDynamicContentSize}
      backgroundStyle={styles.modalSheetBackground}
      handleIndicatorStyle={styles.modalHandleIndicator}
      onChange={handleSheetChange}
      onDismiss={syncClosedState}
    >
      <ModalMenuContext value={menuContext}>{children}</ModalMenuContext>
    </BottomSheetModal>
  );
}

function DesktopModalSurface({
  children,
  close,
  open,
  testID,
}: ModalSurfaceImplementationProps) {
  const windowDimensions = useWindowDimensions();
  const compact = windowDimensions.width < 768;
  const horizontalMargin = 12;
  const maxWidth = Math.min(384, windowDimensions.width - horizontalMargin * 2);
  const maxHeight = Math.max(
    240,
    compact ? windowDimensions.height * 0.84 : windowDimensions.height - 96,
  );

  return (
    <Modal
      transparent
      visible={open}
      animationType="fade"
      presentationStyle="overFullScreen"
      onRequestClose={close}
    >
      <View style={styles.modalOverlay} pointerEvents="box-none">
        <Pressable
          accessibilityLabel="Close menu"
          testID={testID ? `${testID}Backdrop` : undefined}
          style={styles.modalBackdrop}
          onPress={close}
        />
        <View
          pointerEvents="box-none"
          style={styles.modalPositioner({ compact, horizontalMargin })}
        >
          <Box
            background="surface"
            border="muted"
            testID={testID}
            onTouchStart={(event) => {
              event.stopPropagation();
            }}
            style={[
              styles.modalSurface({ compact }),
              { maxHeight, width: compact ? '100%' : maxWidth },
            ]}
          >
            {children}
          </Box>
        </View>
      </View>
    </Modal>
  );
}

function ModalMenuHeader({
  children,
  title,
}: {
  children?: ReactNode;
  title?: string;
}) {
  return (
    <Box style={styles.modalHeader}>
      {children ?? (
        <Text bold size="lg">
          {title}
        </Text>
      )}
    </Box>
  );
}

function ModalMenuBody({ children }: { children: ReactNode }) {
  if (Platform.OS !== 'web') {
    return (
      <BottomSheetScrollView
        keyboardShouldPersistTaps="handled"
        nestedScrollEnabled
        style={styles.modalBody}
        contentContainerStyle={styles.modalBodyContent}
      >
        {children}
      </BottomSheetScrollView>
    );
  }

  return (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      nestedScrollEnabled
      style={styles.modalBody}
      contentContainerStyle={styles.modalBodyContent}
    >
      {children}
    </ScrollView>
  );
}

function ModalMenuFooter({ children }: { children: ReactNode }) {
  return <Box style={styles.modalFooter}>{children}</Box>;
}

type MenuCheckboxFilterProps<TOption extends MenuCheckboxOption> = {
  children: (props: MenuTriggerProps) => ReactElement;
  doneLabel?: string;
  onDone: () => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  sections: readonly MenuCheckboxSection<TOption>[];
  testID?: string;
  title: string;
};

function MenuCheckboxFilter<TOption extends MenuCheckboxOption>({
  children,
  doneLabel = 'Done',
  onDone,
  onOpenChange,
  open,
  sections,
  testID,
  title,
}: MenuCheckboxFilterProps<TOption>) {
  return (
    <ModalMenuRoot open={open} onOpenChange={onOpenChange}>
      <ModalMenuTrigger>{children}</ModalMenuTrigger>
      <ModalMenuSurface testID={testID}>
        <ModalMenuHeader title={title} />
        <ModalMenuBody>
          {sections.map((section) => (
            <Box key={section.id} direction="vertical">
              <Text px="md" py="sm" size="md" variant="typographySecondary">
                {section.title}
              </Text>
              <CheckboxRow
                rounded
                value={section.selectedIds.length === 0}
                testID={testID ? `${testID}${section.id}All` : undefined}
                onValueChange={() => {
                  section.onSelectedIdsChange([]);
                }}
              >
                <Text numberOfLines={1}>{section.allLabel}</Text>
              </CheckboxRow>
              {section.options.length ? (
                section.options.map((option) => {
                  const selected = section.selectedIds.includes(option.id);

                  return (
                    <CheckboxRow
                      key={option.id}
                      rounded
                      disabled={option.disabled}
                      value={selected}
                      testID={
                        testID
                          ? `${testID}${section.id}-${option.id}`
                          : undefined
                      }
                      onValueChange={(nextSelected) => {
                        section.onSelectedIdsChange(
                          toggleSelection({
                            ids: section.selectedIds,
                            selected: nextSelected,
                            value: option.id,
                          }),
                        );
                      }}
                    >
                      <Box
                        direction="horizontal"
                        align="center"
                        minWidth={0}
                        space="sm"
                      >
                        {section.renderLeading?.(option)}
                        <Text flexShrink={1} numberOfLines={1}>
                          {option.label}
                        </Text>
                      </Box>
                    </CheckboxRow>
                  );
                })
              ) : (
                <Text px="md" py="sm" variant="typographySecondary">
                  {section.emptyLabel}
                </Text>
              )}
            </Box>
          ))}
        </ModalMenuBody>
        <ModalMenuFooter>
          <FooterActionButton
            appearance="outline"
            icon="checkmark-outline"
            label={doneLabel}
            testID={testID ? `${testID}DoneButton` : undefined}
            variant="secondary"
            onPress={onDone}
          />
        </ModalMenuFooter>
      </ModalMenuSurface>
    </ModalMenuRoot>
  );
}

export { MenuCheckboxFilter as CheckboxFilter };

function isValidRectangle(rectangle: MenuRectangle) {
  return (
    Number.isFinite(rectangle.height) &&
    Number.isFinite(rectangle.width) &&
    Number.isFinite(rectangle.x) &&
    Number.isFinite(rectangle.y) &&
    rectangle.height > 0 &&
    rectangle.width > 0
  );
}

function toRadixAlign(x: AlignX): 'center' | 'end' | 'start' {
  if (x === 'right') {
    return 'end';
  }

  if (x === 'center') {
    return 'center';
  }

  return 'start';
}

const webContentStyle: CSSProperties = {
  maxHeight: 'var(--radix-dropdown-menu-content-available-height)',
  maxWidth: 'min(20rem, calc(100vw - 1rem))',
  minWidth: '13.75rem',
  zIndex: 1000,
};

function webItemStyle({
  disabled,
  highlighted,
}: WebItemStyleOptions): CSSProperties {
  return {
    alignItems: 'center',
    backgroundColor:
      highlighted && !disabled ? 'rgba(128, 128, 128, 0.16)' : 'transparent',
    borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    opacity: disabled ? 0.6 : 1,
    outline: 'none',
    padding: 12,
    userSelect: 'none',
  };
}

const styles = StyleSheet.create((theme, runtime) => ({
  actionTitle: ({
    destructive,
    disabled,
  }: {
    destructive?: boolean;
    disabled?: boolean;
  }) => ({
    color: destructive
      ? theme.colors.error
      : disabled
        ? theme.colors.typographyMuted
        : theme.colors.typography,
    flexShrink: 1,
    fontSize: theme.fontSize.lg,
  }),
  anchoredBackdrop: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  anchoredContent: {
    paddingHorizontal: theme.size(2),
    paddingVertical: theme.size(2),
  },
  anchoredOverlay: {
    flex: 1,
  },
  anchoredSafeArea: {
    flex: 1,
    paddingBottom: runtime.insets.bottom,
    paddingLeft: runtime.insets.left,
    paddingRight: runtime.insets.right,
    paddingTop: runtime.insets.top,
  },
  anchoredSurface: {
    borderRadius: theme.borderRadius.lg,
    borderWidth: StyleSheet.hairlineWidth,
    elevation: 8,
    overflow: 'hidden',
    position: 'absolute',
    shadowColor: theme.colors.black,
    shadowOffset: { height: theme.size(2), width: 0 },
    shadowOpacity: 0.3,
    shadowRadius: theme.size(4),
  },
  anchoredSurfacePosition: ({
    start,
    top,
  }: {
    start: number;
    top: number;
  }) => ({
    opacity: 1,
    start,
    top,
  }),
  anchoredUnpositionedSurface: {
    left: 0,
    opacity: 0,
    top: 0,
  },
  anchoredViewport: {
    flex: 1,
  },
  itemTitle: {
    color: theme.colors.typography,
    fontSize: theme.fontSize.lg,
  },
  label: {
    color: theme.colors.typographySecondary,
    fontSize: theme.fontSize.md,
    paddingHorizontal: theme.size(3),
    paddingVertical: theme.size(2),
  },
  modalBackdrop: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  modalBody: {
    flexShrink: 1,
  },
  modalBodyContent: {
    gap: theme.space('md'),
    paddingBottom: theme.space('md') + runtime.insets.bottom,
    paddingHorizontal: theme.space('md'),
    paddingTop: theme.space('md'),
  },
  modalFooter: {
    borderTopColor: theme.colors.borderMuted,
    borderTopWidth: theme.size(0.5),
    paddingBottom: theme.space('md') + runtime.insets.bottom,
    paddingHorizontal: theme.space('md'),
    paddingTop: theme.space('md'),
  },
  modalHandleIndicator: {
    backgroundColor: theme.colors.surfaceMuted,
  },
  modalHeader: {
    borderBottomColor: theme.colors.borderMuted,
    borderBottomWidth: theme.size(0.5),
    paddingHorizontal: theme.space('lg'),
    paddingVertical: theme.space('md'),
  },
  modalOverlay: {
    flex: 1,
  },
  modalPositioner: ({
    compact,
    horizontalMargin,
  }: {
    compact: boolean;
    horizontalMargin: number;
  }) => ({
    alignItems: compact ? 'stretch' : 'flex-end',
    bottom: compact ? 0 : undefined,
    left: compact ? 0 : horizontalMargin,
    paddingTop: compact ? undefined : theme.space('4xl'),
    position: 'absolute',
    right: compact ? 0 : horizontalMargin,
    top: compact ? undefined : 0,
  }),
  modalSheetBackground: {
    backgroundColor: theme.colors.surface,
  },
  modalSurface: ({ compact }: { compact: boolean }) => ({
    borderRadius: compact ? 0 : theme.borderRadius.lg,
    borderTopLeftRadius: compact
      ? theme.borderRadius.xl
      : theme.borderRadius.lg,
    borderTopRightRadius: compact
      ? theme.borderRadius.xl
      : theme.borderRadius.lg,
    borderWidth: compact ? 0 : theme.size(0.5),
    elevation: 8,
    overflow: 'hidden',
    shadowColor: theme.colors.black,
    shadowOffset: { height: theme.size(2), width: 0 },
    shadowOpacity: 0.3,
    shadowRadius: theme.size(4),
    _web: {
      cursor: 'default',
    },
  }),
  nativeItem: ({
    disabled,
    pressed,
  }: {
    disabled: boolean;
    pressed: boolean;
  }) => ({
    alignItems: 'center',
    backgroundColor: pressed ? theme.colors.surfaceSecondary : 'transparent',
    borderRadius: theme.borderRadius.md,
    flexDirection: 'row',
    gap: theme.space('md'),
    opacity: disabled ? 0.6 : 1,
    paddingHorizontal: theme.size(3),
    paddingVertical: theme.size(3),
  }),
  webMenu: {
    borderRadius: theme.borderRadius.lg,
    borderWidth: StyleSheet.hairlineWidth,
    boxShadow: `0 ${theme.size(2)}px ${theme.size(4)}px rgba(0, 0, 0, 0.3)`,
    overflow: 'hidden',
    paddingHorizontal: theme.size(2),
    paddingVertical: theme.size(2),
  },
  webScrollContent: {
    flexShrink: 1,
  },
}));
